import * as z from "zod";
import { ianaTimeZoneNameSchema } from "../timezone-list";

// A scheduled promotion on a TRACKING_PAGE flow step. While its window is
// open, the event's tracking page is served instead of the step's default
// page; outside the window the step behaves as if the event didn't exist.
const trackingPageScheduledEventSchema_ = z.object({
  id: z.string(),
  name: z.string(),
  // The tracking page served while this event is live.
  trackingPageId: z.string(),
  // Absolute UTC instants. Coerced because the flow crosses JSON boundaries
  // before the runtime parses it (Temporal workflow payloads,
  // JSON.parse(JSON.stringify(...))), where Dates arrive as ISO strings that
  // plain `z.date()` would reject.
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  // IANA timezone the merchant authored the window in. startAt/endAt are
  // stored as UTC instants; this round-trips the authoring zone for the
  // editor and display labels.
  timeZone: ianaTimeZoneNameSchema,
});
export const trackingPageScheduledEventSchema: z.ZodType<TrackingPageScheduledEvent> =
  trackingPageScheduledEventSchema_;
export interface TrackingPageScheduledEvent
  extends z.infer<typeof trackingPageScheduledEventSchema_> {}

interface ScheduledWindow {
  startAt: Date | string;
  endAt: Date | string;
}

// startAt/endAt may reach the interpreter as ISO strings rather than Date
// objects: the flow crosses JSON boundaries before the interpreter reads it,
// and the steps are not always re-parsed through the coercing schema. Compare
// on millisecond timestamps so a string bound can't silently widen the window
// — `Date < string` evaluates to NaN-comparison `false`.
function windowMs(window: ScheduledWindow): { startMs: number; endMs: number } {
  return {
    startMs: new Date(window.startAt).getTime(),
    endMs: new Date(window.endAt).getTime(),
  };
}

/** Whether `now` falls within the event's window, inclusive on both ends. */
export function isScheduledEventActive(
  event: ScheduledWindow,
  now: Date,
): boolean {
  const { startMs, endMs } = windowMs(event);
  const nowMs = now.getTime();
  return nowMs >= startMs && nowMs <= endMs;
}

/**
 * The event that should serve at `now`, or undefined if none is active. The
 * first active event in array order wins; the editor blocks overlapping
 * windows, so this is only a deterministic tiebreak for legacy/bad data.
 */
export function findActiveScheduledEvent<E extends ScheduledWindow>(
  events: readonly E[] | undefined,
  now: Date,
): E | undefined {
  return events?.find((event) => isScheduledEventActive(event, now));
}

export type ScheduledEventStatus = "live" | "scheduled" | "finished";

/** Status of an event at `now`, using the same comparisons as the runtime. */
export function getScheduledEventStatus(
  event: ScheduledWindow,
  now: Date,
): ScheduledEventStatus {
  const { startMs, endMs } = windowMs(event);
  const nowMs = now.getTime();
  if (nowMs < startMs) {
    return "scheduled";
  }
  if (nowMs > endMs) {
    return "finished";
  }
  return "live";
}

/**
 * Whether any two events' windows intersect. Bounds are inclusive, so windows
 * that merely touch (one's endAt equals another's startAt) count as
 * overlapping.
 */
export function scheduledEventsOverlap(
  events: readonly ScheduledWindow[],
): boolean {
  const windows = events.map(windowMs);
  return windows.some((a, i) =>
    windows
      .slice(i + 1)
      .some((b) => a.startMs <= b.endMs && b.startMs <= a.endMs),
  );
}
